import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { constant } from "common/constant";
import { putApi } from "common/services/api";
import { getApi, postApi } from "common/services/api";
import { API_PATHS } from "common/services/apiPaths";
import { toast } from "react-toastify";
// import S3Helper from "common/utils/s3-helper";
// import { getS3Config, validateS3Config } from "common/utils/s3configuration";

import S3Helper from "common/utils/s3-helper";
import { getS3Config, validateS3Config } from "common/utils/s3Configuration";
import { deleteManyApi } from "common/services/api";

// Helper function to extract S3 key from URL
const extractS3KeyFromUrl = (url) => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    console.log("s3 key url", urlObj.pathname.substring(1))
    return urlObj.pathname.substring(1); // Remove leading slash
  } catch (error) {
    console.error('Invalid URL:', url);
    return null;
  }
};

const resetCandidatePaginationState = () => {
  try {
    const rawPaginationState = localStorage.getItem("paginationState");
    const parsedPaginationState = rawPaginationState
      ? JSON.parse(rawPaginationState)
      : {};

    localStorage.setItem(
      "paginationState",
      JSON.stringify({
        ...parsedPaginationState,
        pageName: "Candidates",
        currentPage: 1,
        pageSize: parsedPaginationState?.pageSize || 10,
      })
    );
  } catch (error) {
    console.error("Failed to reset candidate pagination state:", error);
  }
};


// NEW: Delete multiple candidate profiles with attachment cleanup
export const deleteMultipleCandidates = createAsyncThunk(
  "candidates/deleteMultipleCandidates",
  async (ids, { rejectWithValue, dispatch }) => {
    try {
      // 1. Get all candidate profiles data to find attachments
      const attachmentsToDelete = []

      // Fetch each candidate profile to get attachment URLs
      for (const id of ids) {
        try {
          const candidateResponse = await getApi(`${API_PATHS.CANDIDATE_LIST}${id}`)
          const candidate = candidateResponse.data

          if (candidate.resume) {
            const resumeKey = extractS3KeyFromUrl(candidate.resume)
            if (resumeKey) attachmentsToDelete.push(resumeKey)
          }

          if (candidate.coverLetter) {
            const coverLetterKey = extractS3KeyFromUrl(candidate.coverLetter)
            if (coverLetterKey) attachmentsToDelete.push(coverLetterKey)
          }
        } catch (fetchError) {
          console.warn(`Failed to fetch candidate profile ${id}:`, fetchError)
          // Continue with other profiles even if one fetch fails
        }
      }

      // 2. Delete attachments from S3 if any exist
      if (attachmentsToDelete.length > 0) {
        try {
          const s3Config = getS3Config()
          // validateS3Config(s3Config);
          const s3Helper = new S3Helper(s3Config)

          await s3Helper.deleteMultipleFiles(attachmentsToDelete)
          console.log("Successfully deleted candidate attachments:", attachmentsToDelete)
        } catch (s3Error) {
          console.warn("Failed to delete candidate attachments from S3:", s3Error)
          // Continue with candidate profile deletion even if S3 deletion fails
        }
      }

      // 3. Delete the candidate profiles from the database
      const deleteResponse = await deleteManyApi(`${API_PATHS.CANDIDATE_LIST}deleteMany`, ids)

      if (deleteResponse.status === 200) {
        // const count = ids.length
        // toast.success(`${count} candidate${count > 1 ? "s" : ""} deleted successfully`)
        return ids
      } else {
        toast.error(deleteResponse?.response?.data?.message ?? constant.somethingWentWrong)
        return rejectWithValue("Failed to delete candidate profiles")
      }
    } catch (error) {
      // console.error("Delete Multiple Candidate Profiles Error:", error)
      toast.error(error?.response?.data?.message ?? constant.somethingWentWrong)
      return rejectWithValue(error.message || constant.apiReqFailed)
    }
  },
)

// Existing thunks...
export const fetchCandidateData = createAsyncThunk(
  "recruitment/fetchCandidateData",
  async (_, { rejectWithValue }) => {
    try {
      const result = await getApi(API_PATHS.CANDIDATE_LIST);
      const userResult = await getApi(API_PATHS.USER);

      if (result.data.length > 0) {
        const updatedData = result.data.map((res) => ({
          ...res,
          jobProfileName: res.currentJobTitle || constant.notAllowed,
          hiringManager: userResult.data.user.find((data) => data._id === res.hiringManager)?.username || constant.notAllowed,
          assignedRecruiter: userResult.data.user.find((data) => data._id === res.assignedRecruiter)?.username || constant.notAllowed,
          department: res.department?.name || constant.notAllowed,
        }));

        return updatedData;
      }
      return [];
    } catch (error) {
      return rejectWithValue(constant.somethingWentWrong);
    }
  }
);

export const addCandidate = createAsyncThunk(
  `${API_PATHS.POST_CANDIDATE}`,
  async ({ values, resetForm, navigate }, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState();
      const userId = state.user.user?._id;

      const hasResume = values.resume && values.resume instanceof File;
      const hasCoverLetter = values.coverLetter && values.coverLetter instanceof File;

      if (!hasResume && !hasCoverLetter) {
        const response = await postApi(API_PATHS.POST_CANDIDATE, values);

        if (response.status === 201) {
          resetForm();
          toast.success(constant.candidateCreatedSuccess);
          resetCandidatePaginationState();
          dispatch(fetchCandidateData());
          navigate("/recruitment/candidates");
          return response.data;
        } else {
          toast.error(constant.somethingWentWrong);
          return rejectWithValue(constant.failedToAddProfile);
        }
      }

      const valuesWithoutAttachments = { ...values };
      valuesWithoutAttachments.resume = "--no-file--"; // Placeholder for no file
      valuesWithoutAttachments.coverLetter = "";
      const response = await postApi(API_PATHS.POST_CANDIDATE, valuesWithoutAttachments);

      if (response.status !== 201) {
        toast.error(constant.somethingWentWrong);
        return rejectWithValue(constant.failedToAddProfile);
      }

      const jobId = response.data._id;
      if (!jobId) {
        toast.error(constant.somethingWentWrong);
        return rejectWithValue("Job ID not found in response");
      }

      const s3Config = getS3Config();
      // validateS3Config(s3Config);;
      const s3Helper = new S3Helper(s3Config);

      const attachmentPaths = {};

      try {
        if (hasResume) {
          const resumeResult = await s3Helper.uploadFile(
            values.resume,
            {
              keyPrefix: `/candidate/${jobId}/user/${userId}/`,
              generateUniqueKey: true,
              metadata: {
                jobId: jobId,
                type: 'job-summary'
              }
            }
          );

          if (resumeResult.success) {
            attachmentPaths.resume = resumeResult.url;
          } else {
            throw new Error('Failed to upload job summary attachment');
          }
        }

        if (hasCoverLetter) {
          const otherResult = await s3Helper.uploadFile(
            values.coverLetter,
            {
              keyPrefix: `/others-attachment/${jobId}/user/${userId}/`,
              generateUniqueKey: true,
              metadata: {
                jobId: jobId,
                type: 'cover-letter'
              }
            }
          );

          if (otherResult.success) {
            attachmentPaths.coverLetter = otherResult.url;
          } else {
            throw new Error('Failed to upload other attachment');
          }
        }

        const updateResponse = await putApi(
          `${API_PATHS.POST_CANDIDATE}/${jobId}`,
          attachmentPaths
        );

        if (updateResponse.status === 200) {
          resetForm();
          toast.success(constant.candidateCreatedSuccess);
          resetCandidatePaginationState();
          dispatch(fetchCandidateData());
          navigate("/recruitment/candidates");
          return { ...response.data, ...attachmentPaths };
        } else {
          toast.error(constant.somethingWentWrong);
          return rejectWithValue("Failed to update job profile with attachment paths");
        }

      } catch (uploadError) {
        console.error('S3 Upload Error:', uploadError);
        toast.error("Failed to upload attachments");
        return rejectWithValue(`Upload failed: ${uploadError.message}`);
      }

    } catch (error) {
      console.error('Add Recruitment Profile Error:', error);
      toast.error(constant.somethingWentWrong);
      return rejectWithValue(error.message || constant.apiReqFailed);
    }
  }
);

export const updateCandidate = createAsyncThunk(
  "recruitment/updateCandidate",
  async ({ id, values, resetForm, navigate }, { rejectWithValue, dispatch, getState }) => {
    try {
      console.log("Update values received:", values);

      const state = getState();
      const userId = state.user.user?._id;

      // Prepare update data - values already contain processed S3 keys from frontend
      const updateData = { ...values };

      console.log("Final update data:", updateData);

      // Update candidate in database
      const updateResponse = await putApi(
        `${API_PATHS.POST_CANDIDATE}/${id}`,
        updateData
      );

      if (updateResponse.status === 200) {
        if (resetForm) resetForm();
        toast.success("Candidate updated successfully");
        resetCandidatePaginationState();
        dispatch(fetchCandidateData());
        if (navigate) navigate("/recruitment/candidates");
        return updateResponse.data;
      } else {
        toast.error(updateResponse?.response?.data?.message ?? constant.somethingWentWrong)
      
        return rejectWithValue("Failed to update candidate");
      }
    } catch (error) {
      console.error("Update Candidate Error:", error);
     
      toast.error(error?.response?.data?.message ?? constant.somethingWentWrong)
  
      return rejectWithValue(error.message || constant.apiReqFailed);
    }
  }
);
// NEW: Delete attachment from job profile
export const deleteAttachment = createAsyncThunk(
  "recruitment/deleteAttachment",
  async ({ jobId, attachmentType }, { rejectWithValue, dispatch }) => {
    try {
      // Get current job data
      const currentJobResponse = await getApi(`${API_PATHS.CANDIDATE_LIST}${jobId}`);
      const currentJob = currentJobResponse.data;

      const attachmentUrl = currentJob[attachmentType];
      if (!attachmentUrl) {
        return rejectWithValue("Attachment not found");
      }

      const s3Config = getS3Config();
      // validateS3Config(s3Config);;
      const s3Helper = new S3Helper(s3Config);

      // Delete from S3
      const s3Key = extractS3KeyFromUrl(attachmentUrl);
      if (s3Key) {
        try {
          await s3Helper.deleteFile(s3Key);
        } catch (s3Error) {
          console.warn('Failed to delete from S3:', s3Error);
          // Continue with database update even if S3 deletion fails
        }
      }

      // Update database to remove attachment URL
      const updateData = {
        [attachmentType]: null
      };

      const updateResponse = await putApi(
        `${API_PATHS.POST_CANDIDATE}/${jobId}`,
        updateData
      );

      if (updateResponse.status === 200) {
        toast.success("Attachment deleted successfully");
        dispatch(fetchCandidateData());
        return { jobId, attachmentType };
      } else {
        toast.error(constant.somethingWentWrong);
        return rejectWithValue("Failed to delete attachment");
      }

    } catch (error) {
      console.error('Delete Attachment Error:', error);
      toast.error(constant.somethingWentWrong);
      return rejectWithValue(error.message || constant.apiReqFailed);
    }
  }
);

// NEW: Download attachment
export const downloadAttachment = createAsyncThunk(
  "recruitment/downloadAttachment",
  async ({ attachmentUrl, filename }, { rejectWithValue }) => {
    try {
      const s3Config = getS3Config();
      // // validateS3Config(s3Config);;
      const s3Helper = new S3Helper(s3Config);

      const s3Key = extractS3KeyFromUrl(attachmentUrl);
      console.log("s3Key", s3Key)
      if (!s3Key) {
        return rejectWithValue("Invalid attachment URL");
      }

      const result = await s3Helper.downloadFile(s3Key, {
        downloadAsBlob: true,
        saveAs: filename
      });

      console.log("download result", result)

      if (result.success) {
        toast.success("File downloaded successfully");
        return result;
      } else {
        toast.error("Failed to download file 1");
        return rejectWithValue("Download failed");
      }

    } catch (error) {
      console.error('Download Attachment Error:', error);
      toast.error("Failed to download file 2");
      return rejectWithValue(error.message || "Download failed");
    }
  }
);

const candidateSlice = createSlice({
  name: "candidates",
  initialState: {
    data: [],
    loading: false,
    error: null,
    addLoading: false,
    updateLoading: false,
    deleteLoading: false,
    downloadLoading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCandidateData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCandidateData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchCandidateData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addCandidate.pending, (state) => {
        state.addLoading = true;
      })
      .addCase(addCandidate.fulfilled, (state) => {
        state.addLoading = false;
      })
      .addCase(addCandidate.rejected, (state, action) => {
        state.addLoading = false;
        state.error = action.payload;
      })

      .addCase(updateCandidate.pending, (state) => {
        state.updateLoading = true;
      })
      .addCase(updateCandidate.fulfilled, (state) => {
        state.updateLoading = false;
      })
      .addCase(updateCandidate.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })

      .addCase(deleteAttachment.pending, (state) => {
        state.deleteLoading = true;
      })
      .addCase(deleteAttachment.fulfilled, (state) => {
        state.deleteLoading = false;
      })
      .addCase(deleteAttachment.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      })

      .addCase(deleteMultipleCandidates.pending, (state) => {
        state.deleteLoading = true
      })
      .addCase(deleteMultipleCandidates.fulfilled, (state, action) => {
        state.deleteLoading = false
        state.data = state.data.filter((candidate) => !action.payload.includes(candidate._id))
      })
      .addCase(deleteMultipleCandidates.rejected, (state, action) => {
        state.deleteLoading = false
        state.error = action.payload
      })

      .addCase(downloadAttachment.pending, (state) => {
        state.downloadLoading = true;
      })
      .addCase(downloadAttachment.fulfilled, (state) => {
        state.downloadLoading = false;
      })
      .addCase(downloadAttachment.rejected, (state, action) => {
        state.downloadLoading = false;
        state.error = action.payload;
      });
  },
});

export default candidateSlice.reducer;
